import { ChangeEvent, FC, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogProps,
  Stack,
  Typography,
  Checkbox,
  FormControlLabel,
  Box,
  useMediaQuery,
  Theme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { CheckboxCheckedIcon, CheckboxIcon } from 'assets';

export interface AINotesTermsAndConditionsConfirmationDialogProps extends DialogProps {
  onConfirm(): void;
  onCancel(): void;
}

export const AINotesTermsAndConditionsConfirmationDialog: FC<AINotesTermsAndConditionsConfirmationDialogProps> = ({
  onConfirm,
  onCancel,
  ...props
}) => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const { t } = useTranslation('aiNotesTerms');

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setIsChecked(event.target.checked);
  };

  const handleConfirm = async (): Promise<void> => {
    onConfirm?.();
  };

  return (
    <Dialog
      fullScreen={isMobile}
      {...props}
      sx={{
        zIndex: 9999,
        '.MuiPaper-root': {
          maxWidth: '1100px',
          width: '100%',
        },
      }}
    >
      <DialogContent
        sx={({ breakpoints }) => ({
          padding: '37px 29px 27px 46px',
          maxWidth: '100%',
          width: '100%',
          [breakpoints.down(599)]: {
            display: 'grid',
            gridTemplateColumns: '1fr',
            gridTemplateRows: '.6fr auto .6fr',
            padding: '37px 29px 27px 36px',
          },
          [breakpoints.down(391)]: {
            gridTemplateRows: '.4fr auto .3fr',
            paddingBlock: '27px',
            paddingInlineStart: '16px',
            paddingInlineEnd: '14px',
          },
        })}
      >
        <Stack
          direction='column'
          justifyContent='space-between'
          gap='8px'
          sx={({ breakpoints }) => ({
            [breakpoints.down(599)]: {
              gridColumnStart: 1,
              gridColumnEnd: 2,
              gridRowStart: 1,
              gridRowEnd: 2,
            },
          })}
        >
          <Typography
            sx={{
              color: 'var(--app-brand-primary)',
              fontSize: '24px',
              fontWeight: 700,
              lineHeight: '36px',
              textAlign: 'left',
            }}
          >
            {t('title')}
          </Typography>
        </Stack>

        <Box
          sx={({ breakpoints }) => ({
            height: 408,
            overflow: 'auto',
            marginBlockStart: '18px',
            marginBlockEnd: '36px',
            paddingRight: '46px',
            [breakpoints.down(768)]: {
              paddingRight: '18px',
              marginBlockEnd: '18px',
            },
            [breakpoints.down(599)]: {
              gridColumnStart: 1,
              gridColumnEnd: 2,
              gridRowStart: 2,
              gridRowEnd: 3,
              height: 'calc(100vh - 286px)',
            },
          })}
        >
          <Typography
            component='div'
            sx={{
              color: 'var(--app-text-default)',
              fontSize: '16px',
              fontWeight: 500,
              lineHeight: '25.5px',
              textAlign: 'justify',
              a: {
                color: 'var(--app-brand-primary)',
                textDecoration: 'underline',
                margin: 0,
              },
            }}
            dangerouslySetInnerHTML={{ __html: t('html') }}
          />
        </Box>

        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          gap='16px'
          sx={({ breakpoints }) => ({
            [breakpoints.down(768)]: {
              flexDirection: 'column',
              paddingRight: '18px',
            },
            [breakpoints.down(599)]: {
              gridColumnStart: 1,
              gridColumnEnd: 2,
              gridRowStart: 3,
              gridRowEnd: 4,
            },
          })}
        >
          <Stack direction='row'>
            <FormControlLabel
              sx={({ breakpoints }) => ({
                [breakpoints.down(768)]: {
                  marginRight: 0,
                },
              })}
              label={
                <Typography
                  sx={({ breakpoints }) => ({
                    color: 'var(--app-text-default)',
                    fontSize: '17px',
                    fontWeight: 700,
                    lineHeight: '25.5px',
                    textAlign: 'left',
                    [breakpoints.down(768)]: {
                      textAlign: 'justify',
                    },
                  })}
                >
                  {t('confirmLabel')}
                </Typography>
              }
              control={
                <Checkbox
                  checked={isChecked}
                  onChange={handleCheckboxChange}
                  icon={<CheckboxIcon />}
                  checkedIcon={<CheckboxCheckedIcon />}
                />
              }
            />
          </Stack>
          <Stack direction='row' alignItems='center' justifyContent='space-between' gap='8px'>
            <Button
              variant='text'
              sx={{
                width: 167,
                height: 44,
                fontWeight: 600,
                fontSize: 17,
                borderRadius: 1,
              }}
              onClick={onCancel}
            >
              {t('cancel')}
            </Button>
            <Button
              variant='contained'
              disabled={!isChecked}
              sx={{
                width: 167,
                height: 44,
                fontWeight: 600,
                fontSize: 17,
                borderRadius: 1,
                ':disabled': {
                  opacity: '0.25 !important',
                  backgroundColor: 'var(--app-brand-primary) !important',
                },
              }}
              onClick={handleConfirm}
            >
              {t('accept')}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
